import React from "react";
import { Avatar, Box, Chip, Grid, Paper, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BrushOutlinedIcon from "@mui/icons-material/BrushOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Link } from "react-router-dom";

import { CoreNetworkMemberAvatar, MetricCard, PagedTable, SectionCard, StatusChip, buildCoreNetworkPath, formatDateTime, toTitleCase } from "../coreNetworkUiShared";

export const BINATED_REPORTING_TOOLTIP = "Binated requests are artwork jobs submitted to the configured external Binated provider. They are included here for reporting only and do not use Core Network assignments, offers, or payouts.";

export function buildArtworkReportingPath(instanceUniqueId, record) {
  if (record?.source === "core-network") return buildCoreNetworkPath(instanceUniqueId, `work-requests/${record.sourceRecordId}`);
  if (record?.source !== "binated" || !record.orderId) return null;
  if (String(record.documentType || "").toLowerCase() === "sales-order" && record.documentId) {
    return `/instance/${instanceUniqueId}/sales/orders/${record.orderId}/sales-order/${record.documentId}`;
  }
  return `/instance/${instanceUniqueId}/sales/orders/${record.orderId}/overview`;
}

export function buildArtworkOrderPath(instanceUniqueId, record) {
  // All linked orders use their project-facing OrderId, not a DB primary key.
  const orderId = String(record?.orderId || '').trim();
  if (!instanceUniqueId || !orderId) return null;
  return `/instance/${encodeURIComponent(instanceUniqueId)}/sales/orders/${encodeURIComponent(orderId)}/overview`;
}

export function formatArtworkActivityAgo(value, now = Date.now()) {
  const timestamp = new Date(value || '').getTime();
  if (!Number.isFinite(timestamp)) return '-';
  const seconds = Math.max(0, Math.floor((Number(now) - timestamp) / 1000));
  if (seconds < 60) return 'just now';
  const units = [
    ['year', 365 * 24 * 60 * 60],
    ['month', 30 * 24 * 60 * 60],
    ['week', 7 * 24 * 60 * 60],
    ['day', 24 * 60 * 60],
    ['hour', 60 * 60],
    ['minute', 60],
  ];
  for (const [unit, duration] of units) {
    if (seconds >= duration) {
      const count = Math.floor(seconds / duration);
      return `${count} ${unit}${count === 1 ? '' : 's'} ago`;
    }
  }
  return 'just now';
}

function PersonProfile({ person, to, isTalent = false }) {
  if (!person) return <Typography variant="caption" color="text.secondary">Not recorded</Typography>;
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 125 }}>
      {isTalent ? (
        <CoreNetworkMemberAvatar member={person} size={30} />
      ) : (
        <Avatar src={person.profileImageUrl || undefined} sx={{ width: 30, height: 30, fontSize: 11, bgcolor: '#d9e4f5', color: '#103a71' }}>
          {String(person.displayName || '?').split(/\s+/).map((part) => part[0]).slice(0, 2).join('').toUpperCase()}
        </Avatar>
      )}
      <Typography
        variant="body2"
        component={to ? Link : 'span'}
        to={to || undefined}
        sx={{ fontWeight: 600, color: to ? '#103a71' : 'text.primary', textDecoration: 'none', '&:hover': to ? { textDecoration: 'underline' } : {} }}
      >
        {person.displayName || '-'}
      </Typography>
    </Stack>
  );
}

function formatTurnaround(value) {
  const hours = Number(value);
  if (!Number.isFinite(hours)) return "-";
  if (hours < 24) return `${hours}h`;
  return `${Math.round((hours / 24) * 10) / 10}d`;
}

export default function CoreNetworkArtworkReporting({ report, source = "all", onSourceChange, instanceUniqueId }) {
  const summary = report?.summary || {};
  const rows = (Array.isArray(report?.records) ? report.records : []).slice().sort((a, b) => {
    const delta = new Date(b.lastActivityAt || 0).getTime() - new Date(a.lastActivityAt || 0).getTime();
    return delta || String(a.id || "").localeCompare(String(b.id || ""));
  });
  return (
    <Stack spacing={3}>
      <SectionCard
        title="Artwork workload"
        subtitle="A polished view of artwork flow across your external talent network and the configured Binated provider."
        action={(
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Tooltip title={BINATED_REPORTING_TOOLTIP}><InfoOutlinedIcon color="action" fontSize="small" aria-label="About Binated reporting" /></Tooltip>
            <ToggleButtonGroup exclusive size="small" value={source} onChange={(_event, value) => value && onSourceChange?.(value)} aria-label="Artwork reporting source">
              <ToggleButton value="all">All Sources</ToggleButton>
              <ToggleButton value="core-network">Core Network</ToggleButton>
              <ToggleButton value="binated">Binated</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        )}
      >
        <Stack spacing={2.5}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3.5,
              border: "1px solid #d8e3f0",
              overflow: "hidden",
              background:
                "radial-gradient(circle at top left, rgba(16,58,113,0.12), transparent 30%), linear-gradient(135deg, #f6f9ff 0%, #ffffff 54%, #f8fbff 100%)",
            }}
          >
            <Box sx={{ p: { xs: 2.25, md: 3 } }}>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: "column", lg: "row" }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", lg: "center" }}
                >
                  <Stack spacing={1.2} sx={{ maxWidth: 760 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 3,
                          display: "grid",
                          placeItems: "center",
                          bgcolor: "#103a71",
                          color: "#fff",
                          boxShadow: "0 10px 24px rgba(16, 58, 113, 0.18)",
                        }}
                      >
                        <AutoAwesomeIcon fontSize="small" />
                      </Box>
                      <Chip
                        size="small"
                        label="Artwork intelligence"
                        sx={{
                          bgcolor: "rgba(16, 58, 113, 0.08)",
                          color: "#103a71",
                          fontWeight: 700,
                          border: "1px solid rgba(16, 58, 113, 0.12)",
                        }}
                      />
                    </Stack>
                    <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
                      See what is moving through artwork, proofing, and provider handoff before it becomes a bottleneck.
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Track live execution, revision pressure, and turnaround across your external design pipeline without mixing operational systems together.
                    </Typography>
                  </Stack>

                  <Stack spacing={1} sx={{ minWidth: { lg: 280 } }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <BrushOutlinedIcon sx={{ color: "#103a71", fontSize: 18 }} />
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Source-aware artwork reporting
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      Core Network and Binated remain operationally separate. This view gives leadership-level visibility without blending assignment or payout workflows.
                    </Typography>
                  </Stack>
                </Stack>

                <Grid container spacing={2}>
                  {[
                    {
                      label: "Total artwork volume",
                      value: summary.total || 0,
                      helperText: "All tracked artwork activity in the selected source view",
                      icon: <InsightsOutlinedIcon fontSize="small" />,
                      accent: "#103a71",
                      tone: "rgba(16, 58, 113, 0.08)",
                    },
                    {
                      label: "Core Network flow",
                      value: summary.coreNetwork || 0,
                      helperText: "Jobs routed through your external talent network",
                      icon: <BrushOutlinedIcon fontSize="small" />,
                      accent: "#0f766e",
                      tone: "rgba(15, 118, 110, 0.08)",
                    },
                    {
                      label: "Binated flow",
                      value: summary.binated || 0,
                      helperText: "Reporting only",
                      icon: <InfoOutlinedIcon fontSize="small" />,
                      accent: "#7c3aed",
                      tone: "rgba(124, 58, 237, 0.08)",
                    },
                    {
                      label: "Average turnaround",
                      value: formatTurnaround(summary.averageTurnaroundHours),
                      helperText: `Revisions: ${summary.totalRevisions || 0} • Completed: ${summary.completed || 0}`,
                      icon: <AutoAwesomeIcon fontSize="small" />,
                      accent: "#d97706",
                      tone: "rgba(217, 119, 6, 0.08)",
                    },
                  ].map((card) => (
                    <Grid item xs={12} md={6} lg={3} key={card.label}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          height: "100%",
                          borderRadius: 3,
                          border: "1px solid rgba(16, 24, 40, 0.08)",
                          bgcolor: "rgba(255,255,255,0.86)",
                          backdropFilter: "blur(6px)",
                        }}
                      >
                        <Stack spacing={1.25}>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {card.label}
                              </Typography>
                              <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.75 }}>
                                {card.value}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                width: 38,
                                height: 38,
                                borderRadius: 3,
                                display: "grid",
                                placeItems: "center",
                                color: card.accent,
                                bgcolor: card.tone,
                              }}
                            >
                              {card.icon}
                            </Box>
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                            {card.helperText}
                          </Typography>
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            </Box>
          </Paper>

          <Grid container spacing={2}>
            <Grid item xs={6} md={3}><MetricCard label="Submitted" value={summary.submitted || 0} /></Grid>
            <Grid item xs={6} md={3}><MetricCard label="In progress" value={summary.inProgress || 0} /></Grid>
            <Grid item xs={6} md={3}><MetricCard label="Completed" value={summary.completed || 0} /></Grid>
            <Grid item xs={6} md={3}><MetricCard label="Revision requested" value={summary.revisionRequested || 0} /></Grid>
            <Grid item xs={6} md={3}><MetricCard label="Failed / cancelled" value={summary.failedOrCancelled || 0} /></Grid>
            <Grid item xs={6} md={3}><MetricCard label="Total revisions" value={summary.totalRevisions || 0} /></Grid>
          </Grid>
        </Stack>
      </SectionCard>

      <SectionCard title="Recent artwork activity" subtitle="Read-only reporting rows. Source systems remain operationally separate.">
        <PagedTable
          rows={rows}
          columns={[
            {
              key: "source",
              label: "Source",
              render: (row) => <StatusChip value={row.source} />,
            },
            {
              key: "request",
              label: "Request",
              render: (row) => {
                const path = buildArtworkReportingPath(instanceUniqueId, row);
                const label = `${row.source === "binated" ? "Binated" : "Request"} #${row.sourceRecordId}`;
                return (
                  <Stack spacing={0.35}>
                    {path ? <Typography component={Link} to={path} sx={{ fontWeight: 800, color: "#103a71", textDecoration: "none" }}>{label}</Typography> : <Typography variant="body2" sx={{ fontWeight: 800 }}>{label}</Typography>}
                    <Typography variant="caption" color="text.secondary">{row.execution}</Typography>
                  </Stack>
                );
              },
            },
            {
              key: "orderId", label: "Order",
              render: (row) => {
                const path = buildArtworkOrderPath(instanceUniqueId, row);
                return path ? (
                  <Typography component={Link} to={path} variant="body2" sx={{ color: '#103a71', fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                    #{row.orderId}
                  </Typography>
                ) : '-';
              },
            },
            {
              key: "requestedBy", label: "Requested by",
              render: (row) => row.requestedBy ? (
                <PersonProfile person={row.requestedBy} to={`/profile/${row.requestedBy.id}`} />
              ) : (
                <Tooltip title={row.source === 'binated' ? 'Binated lifecycle records do not identify the internal user who initiated the request.' : 'No originating user is recorded for this request.'}>
                  <Typography variant="caption" color="text.secondary">Not recorded</Typography>
                </Tooltip>
              ),
            },
            {
              key: "talent", label: "Talent",
              render: (row) => row.source === "binated" ? (
                <Tooltip title="Binated is an external provider and does not have a Core Network Talent profile.">
                  <Typography variant="caption" color="text.secondary">External provider</Typography>
                </Tooltip>
              ) : row.talent?.length ? (
                <Stack spacing={0.75}>
                  {row.talent.map((member) => (
                    <PersonProfile
                      key={member.id}
                      person={member}
                      isTalent
                      to={buildCoreNetworkPath(instanceUniqueId, `talent/${member.id}`)}
                    />
                  ))}
                </Stack>
              ) : <Typography variant="caption" color="text.secondary">Unassigned</Typography>,
            },
            { key: "document", label: "Document", render: (row) => [toTitleCase(row.documentType), row.documentId].filter((value) => value && value !== "-").join(" / ") || "-" },
            { key: "providerName", label: "Provider", render: (row) => row.providerName || "-" },
            { key: "normalizedStatus", label: "Reporting status", render: (row) => <StatusChip value={row.normalizedStatus} /> },
            { key: "rawStatus", label: "Raw status", render: (row) => toTitleCase(row.rawStatus) },
            { key: "revisionCount", label: "Revisions", render: (row) => Number(row.revisionCount || 0) },
            {
              key: "lastActivityAt", label: "Last activity",
              render: (row) => (
                <Stack spacing={0.25}>
                  <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.lastActivityAt)}</Typography>
                  <Typography variant="caption" color="text.secondary">{formatArtworkActivityAgo(row.lastActivityAt)}</Typography>
                </Stack>
              ),
            },
          ]}
          emptyTitle={source === "binated" ? "No Binated requests found" : "No artwork requests found"}
          emptyDescription={source === "binated" ? "No Binated requests were found for this instance and date range." : "Artwork reporting will appear here as requests are submitted."}
        />
      </SectionCard>
    </Stack>
  );
}
